/**
 * The Guts Check in Turkish, written 2026-08-31 on the editor panel's request.
 *
 * A deep partial of `gutsCheck`, the same way `diagnostic.tr.ts` is of the
 * Diagnostic. **Only strings.** The scoring is positional — each option is
 * worth its index, 0–3 — so the four options of every question stay in the same
 * order as the English file and nothing here can change what the tool measures.
 *
 * Written rather than translated, per the brief: the sentences are Turkish, but
 * a business word a Turkish executive already says in English stays in English —
 * agent, agentic AI, pilot, copilot, board, CEO, CFO, P&L, API, IT, governance
 * is "yönetişim" because that one *is* said in Turkish. "Green-field" stays,
 * because "sıfırdan kurmak" is the explanation of it and both fit in the line.
 */
import type { GutsDim, GutsLevel, GutsQuestion } from './guts-check'

export const gutsCheckTr = {
  dims: [
    { name: 'Hedef', sub: 'Yönetim nereyi işaret ediyor' },
    { name: 'Karar ve sahiplik', sub: 'Kim imzaladı, karşılığında neyi göze aldı' },
    { name: 'İcra', sub: 'Her gün gerçekten ne çalışıyor' },
    { name: 'Kanıt', sub: 'Bir CFO’nun karşısında savunabileceğiniz etki' },
  ] as GutsDim[],

  questions: [
    {
      dim: 0,
      text: 'Board’unuz önümüzdeki 24 ayda AI’dan ne bekliyor?',
      hint: 'Temenni değil, beklenti. Bunun hesabını gerçekten birine sorarlar mıydı?',
      options: [
        'Board gündeminde ciddi bir başlık olmadı.',
        'Bugünkü süreçlerin içinde ölçülebilir maliyet ve verimlilik kazancı.',
        'Ana süreçlerin işleyişine yerleşmiş AI; sahibi ve hedefi belli.',
        'Esastan farklı bir işletme modeli; işin bir kısmı beyaz sayfadan yeniden tasarlanmış.',
      ],
    },
    {
      dim: 0,
      text: 'Agentic AI umduğunuz her şeyi verseydi, ne değişmiş olurdu?',
      hint: 'Peşinde olduğunuz ödülün büyüklüğü konusunda dürüst olun.',
      options: [
        'Yapısal olarak pek bir şey. Rakiplerin gerisinde kalmadığımıza sevinirdik.',
        'Aynı iş, hizmet maliyeti belirgin biçimde düşmüş halde.',
        'Aynı iş; ama farklı bir süreç ve farklı bir organizasyon tasarımıyla yürüyor.',
        'Bugün yapamadığımız şeylerde rekabet ederdik: yeni teklifler, yeni ekonomi.',
      ],
    },
    {
      dim: 0,
      text: 'Üst ekibiniz bu hedefi yüksek sesle nasıl anlatırdı?',
      hint: 'Kelimeler önemli: kimin ne kadarını teklif etmeye cesaret edeceğini onlar belirliyor.',
      options: [
        'Böyle bir cümle henüz kurulmadı.',
        'Verimlilik: aynı işleri daha ucuza ve daha hızlı yapmak.',
        'Olgunluk: ana süreçleri uçtan uca daha yetkin hale getirmek.',
        'Yeniden inşa: birimi ya da süreci agent’lar etrafında sıfırdan kurmak.',
      ],
    },
    {
      dim: 1,
      text: 'Agentic AI gündeminin sahibi kim?',
      hint: 'Sahiplik demek; bir isim, bir yetki ve bir sonuç demek.',
      options: [
        'Kimse. Arada bir IT konusu olarak gündeme geliyor.',
        'IT ya da inovasyon; birçok girişimden biri olarak.',
        'Yetkisi ve bütçe kalemi olan, adı konmuş bir üst yönetici.',
        'CEO ve üst yönetim; P&L sahipleri sonuçtan tek tek sorumlu.',
      ],
    },
    {
      dim: 1,
      text: 'Yönetim, AI yüzünden neyi durdurmaya, kapatmaya ya da yeniden kurmaya karar verdi?',
      hint: 'Eklemek kolay. Asıl bedeli olan karar, çıkarmaktır.',
      options: [
        'Hiçbir şey; bu soru hiç sorulmadı.',
        'Henüz yok; AI şimdiye kadar mevcut süreçlerin üzerine eklendi.',
        'Bazı süreçler yeniden tasarlandı, bazı roller değişti.',
        'Sistem kapattık, birim yeniden kurduk, insan yerine taşıdık; bilinçli ve görünür şekilde.',
      ],
    },
    {
      dim: 1,
      text: 'İş nasıl finanse ediliyor?',
      hint: 'Bir kurumun niyetini en güvenilir anlatan şey bütçesidir.',
      options: [
        'Ayrılmış bir bütçe yok.',
        'Proje bazında, çoğunlukla başka yerden artan paradan.',
        'Planlama döneminde savunulan, çok yıllı bir program bütçesi.',
        'Board kilometre taşlarına bağlanmış taahhüt edilmiş sermaye ve kabul edilmiş bir kısa vadeli marj etkisi.',
      ],
    },
    {
      dim: 2,
      text: 'Gerçek kullanıcıların her gün gerçek iş yaptığı, canlıda ne var?',
      hint: 'Canlı demek, durduğunda birinin işinin aksaması demek.',
      options: [
        'Hiçbir şey. Kişiler kendi inisiyatifiyle halka açık chatbot’ları kullanıyor.',
        'Geniş kitleye açılmış lisanslı bir copilot; kullanım isteğe bağlı ve dengesiz.',
        'Gerçek iş akışlarının içinde çalışan, insana devri tanımlı birkaç agent.',
        'Agent’lar işletme modelinin standart parçası: ortak bir platformda, birimler arasında yeniden kullanılıyor.',
      ],
    },
    {
      dim: 2,
      text: 'Agent’lar verinize ve ana sistemlerinize erişebiliyor mu?',
      hint: 'Agentic AI, modelden çok tesisatta tökezler.',
      options: [
        'Bunu hiç değerlendirmedik.',
        'Manzaranın dağınık olduğunu biliyoruz; iş henüz başlamadı.',
        'Ana veri alanları temizleniyor, yönetişime alınıyor ve API’lerle açılıyor.',
        'Agent’lar yönetişim altında ana sistemlerden güvenle okuyup onlara yazıyor.',
      ],
    },
    {
      dim: 2,
      text: 'Risk, hukuk ve denetim bu işin neresinde?',
      hint: 'Yönetişim ya bir fren ya da bir ray. Sizde hangisi?',
      options: [
        'AI araçlarını yönetmek yerine erişimi kapattık.',
        'Bir politika dokümanı var; nasıl uygulandığı ve yorumlandığı belirsiz.',
        'Net kurallar, insanın devrede olduğu noktalar ve çeyreklerle değil haftalarla ölçülen bir onay yolu.',
        'Risk ve denetim ilk günden işin tasarımında; yönetişim bizi hızlandırıyor.',
      ],
    },
    {
      dim: 3,
      text: 'AI’ın etkisini nasıl ölçüyorsunuz?',
      hint: 'Birinin bütçesine girmemiş hiçbir şey henüz sonuç değildir.',
      options: [
        'Ölçmüyoruz.',
        'Faaliyet: dağıtılan lisans, giriş sayısı, başlatılan pilot adedi.',
        'Süreç: yeniden tasarlanan akışlarda çevrim süresi, işlem başı maliyet, kalite, hata oranı.',
        'P&L: tasarruf ve büyüme, sorumlu yöneticinin bütçesinde duruyor.',
      ],
    },
    {
      dim: 3,
      text: 'Pilotların sonu ne oluyor?',
      hint: 'Kalıcı pilotlardan oluşan bir portföy, karar vermemenin kibar hali.',
      options: [
        'Adı anılacak bir pilot yok.',
        'Çok pilot var, azı mezun oluyor ve hiçbirini usulünce durdurmuyoruz.',
        'Net bir eşik: pilot ya belirli sürede ölçekleniyor ya kapatılıyor.',
        'Varsayılanımız ölçeklemek; gerekçe isteyen seçenek yatırım yapmamak.',
      ],
    },
    {
      dim: 3,
      text: 'Organizasyonun kendisi nasıl tepki veriyor?',
      hint: 'Bunun gerçek hayatta ayakta kalıp kalmayacağına organizasyonun ortası karar verir.',
      options: [
        'Baskın tepki temkin ve kaçınma.',
        'Yer yer merak var; yönetici katmanı ikna olmuş değil.',
        'Yöneticiler kendi süreçlerini agent’larla yeniden tasarlamak üzere yetiştiriliyor.',
        'Ekipler merkezi bir programı beklemeden agentic değişiklikleri kendileri öneriyor ve devreye alıyor.',
      ],
    },
  ] as GutsQuestion[],

  ladder: [
    { name: 'Kayıtsızlık', desc: 'Hiçbir şeyi değiştirmek istemiyorum.' },
    { name: 'Verimlilik', desc: 'İsrafı, gecikmeyi, iş gücünü azalt.' },
    { name: 'Olgunluk', desc: 'Akışı agent’lar etrafında yeniden tasarla.' },
    { name: 'Yeniden İnşa', desc: 'Sıfırdan, beyaz sayfadan kur.' },
  ],

  levels: [
    {
      band: 'ignorance',
      name: 'Kayıtsızlık',
      headline: 'Teknolojide geri değilsiniz. Kararda gerisiniz.',
      body: [
        'Cevaplarınızda kurumunuzun beceriksiz olduğuna dair hiçbir şey yok. Olan şu: agentic AI henüz kimsenin sorunu haline getirilmemiş. Odada alınmayan karar da her çeyrek kendiliğinden alınıyor.',
        'Bu, rakipleriniz de aynı yerde durduğu sürece savunulabilir bir pozisyon. İçlerinden biri maliyet kısmaktan maliyet yapısını değiştirmeye geçtiği anda aradaki fark teknoloji farkı olmaktan çıkıp ekonomi farkına dönüşür. Onu kapatmak çok daha zordur: o noktada hem sizden fazla harcayabilir hem de sizin altınızda fiyat verebilirler.',
        'İlk hamle bir platform, bir tedarikçi ya da bir pilot değil. Adı konmuş bir üst yönetici, bir yetki ve bir tarih.',
      ],
      provocation:
        'Doğrudan bir rakibiniz gelecek çeyrekte agent’larla hizmet maliyetinden %30 düşürdüğünü açıklasa, ilk 30 günde ne yapardınız — ve bunu bugün yapmanızın önünde ne var?',
      moves: [
        {
          title: 'Sahibini belirleyin.',
          text: 'Tek bir üst yönetici, kayda geçmiş biçimde; yetkisi bir sonraki yeniden yapılanmadan sağ çıkacak kadar net olsun.',
        },
        {
          title: 'Yirmi değil, iki süreç seçin.',
          text: 'Hacmi yüksek, kuralı bol, ölçülebilir. Kimse modele dokunmadan önce mevcut halini düzgünce ölçün.',
        },
        {
          title: 'Deney değil, karar finanse edin.',
          text: 'Küçük bir bütçe ve net bir tarih: o gün ya ölçekleyeceksiniz ya duracaksınız. Bunu açıkça söyleyin.',
        },
      ],
    },
    {
      band: 'efficiency',
      name: 'Verimlilik',
      headline: 'Siz AI’ı daha az israf etmek için kullanıyorsunuz. Rakipleriniz farklılaşmak için kullanıyor.',
      body: [
        'Hakkını verelim: başladınız ve elde ettiğiniz tasarruf muhtemelen gerçek. Verimlilik, bütçe toplantısında savunması en kolay hedeftir; aynı zamanda başkasının kopyalaması en ucuz olanıdır.',
        'Bu seviyenin riski sinsidir. Bir süreci otomatikleştirmek, o sürece zımnen “doğru” demektir. Bunu yeterince uzun yaparsanız bugünkü çalışma biçiminizi daha hızlı, daha ucuz ve değiştirmesi çok daha zor hale getirmiş olursunuz: keçi yoluna asfalt dökmüşsünüz demektir.',
        'Hiçbir kurum kendini bir tasarruf hedefinden yola çıkarak yeniden kurmadı. Bir noktada birinin “bu iş bugün tasarlansaydı nasıl görünürdü?” diye sorması ve cevabı verme yetkisini alması gerekir.',
      ],
      provocation:
        'AI sayesinde tümüyle yapmayı bıraktığınız bir şey söyleyin. Söyleyemiyorsanız israfınızı ortadan kaldırmamış, otomatikleştirmişsiniz demektir.',
      moves: [
        {
          title: 'Görevlerden akışlara geçin.',
          text: 'Uçtan uca tek bir yolculuğu — siparişten tahsilata, tekliften poliçeye, işe alımdan verime — adım adım değil, bütün olarak yeniden tasarlayın.',
        },
        {
          title: 'Neyi saydığınızı değiştirin.',
          text: 'Lisans ve giriş sayılarını rafa kaldırın. Dokunduğunuz akışlarda çevrim süresini, işlem başı maliyeti ve kaliteyi raporlayın.',
        },
        {
          title: 'Yeniden tasarımı inovasyona değil, sahaya verin.',
          text: 'Sonucun sahibi süreç sahibi olmalı; yoksa ilk yoğun çeyrekte bu iş ayakta kalmaz.',
        },
      ],
    },
    {
      band: 'maturity',
      name: 'Olgunluk',
      headline: 'Gerçek bir şey kurdunuz. Asıl soru, onu koruyacak mısınız yoksa ileri mi taşıyacaksınız.',
      body: [
        'Bu, çoğunluğun geldiği yerden ileride. Gerçek iş akışlarında agent’larınız, bir sahibiniz, bir bütçeniz ve bir CFO’nun tanıyacağı metrikleriniz var. Bunlar karar gerektirdi; kararların da siyasi bir bedeli oldu.',
        'Bu seviyenin tipik başarısızlığı çöküş değil, konfor. Kurum, hiç ciddi biçimde sorgulamadığı süreçlerin içinde agent çalıştırmakta ustalaşır; yönetişim bir kuyruğa dönüşür; program da değiştirmek için kurulduğu işin değil kendi devamlılığının peşine düşer.',
        '3. seviye bunun daha fazlası değil, başka bir soru: “bu birimi nasıl iyileştiririz?” değil, “bu birimi bugün baştan kurar mıydık, kurarsak nasıl?” Bu soru rahatsız eder, çünkü cevabı genelde çoktan yatırım yaptığınız bir şeyi işaret eder.',
      ],
      provocation:
        'Bu birimi bugün kuruyor olsaydınız hangi süreçlerinizi yeniden kurmazdınız — ve onları tam olarak ne ayakta tutuyor?',
      moves: [
        {
          title: 'Bir tanesini sıfırdan kurun.',
          text: 'Tek bir birimi ya da ürün hattını agent öncelikli olarak beyaz sayfadan tasarlayın; gerçek bir P&L ve gerçek bir tarihle.',
        },
        {
          title: 'Ölçeklemeyi varsayılan yapın.',
          text: 'İspat yükünü tersine çevirin: yazılı gerekçe isteyen seçenek, yatırım yapmamak olsun.',
        },
        {
          title: 'Yetkinliği sahaya taşıyın.',
          text: 'Agent’ı yalnızca merkezdeki ekip kurabiliyorsa elinizde bir yetkinlik değil, bütçesi olan bir darboğaz var.',
        },
      ],
    },
    {
      band: 'reinvention',
      name: 'Yeniden İnşa',
      headline: 'Zor kısım artık teknoloji değil. Soğukkanlılığınızı korumak.',
      body: [
        'Az sayıda kurumun olduğu yerdesiniz: hedef, sahiplik, icra ve kanıt aynı hizada; organizasyon yapısal değişimi konuşmakla kalmayıp içine sindirmiş.',
        'Risk profili buradan sonra tersine döner. Tehdit atalet değil, geri kayma: zor bir çeyrek, bir yönetim değişikliği, bir olay — ve eski işletme modeli istisna yönetimi, gölge süreçler ve geri çağrılan geçici çözümler üzerinden sessizce geri gelir. Yeniden inşa nadiren bir kararla geri alınır; sürüklenerek geri alınır.',
        'İkinci risk darlık. Bir birimdeki başarı çoğu zaman şablon değil vitrin olur. Bileşik getiri sağlayan kurumlar, sıfırdan kurmayı yeni varsayılan sayan ve diğer her birime “neden farklısın?” sorusunu sorduran kurumlardır.',
      ],
      provocation:
        'İşin neresinde eski işletme modeli sessizce geri geliyor — ve buna göz yumduğu için kim ödüllendiriliyor?',
      moves: [
        {
          title: 'Sıfırlamayı kurumsallaştırın.',
          text: 'Beyaz sayfa sorusunu yıllık planlama döngüsüne koyun ki kişiye değil sürece bağlı sorulsun.',
        },
        {
          title: 'Başarısızlığı değil, kaymayı izleyin.',
          text: 'İstisnaları, manuel müdahaleleri ve geçici çözümleri takip edin. Eski modelin geri geldiğini önce onlar haber verir.',
        },
        {
          title: 'Modeli dışa taşıyın.',
          text: 'Başarılı birimi, sunumda duran bir vaka değil, yetkisi olan tekrarlanabilir bir şablon haline getirin.',
        },
      ],
    },
  ] as GutsLevel[],

  gap: {
    aheadTitle: 'Hedef ile gerçek arasındaki fark',
    aheadBody: [
      'Söylediğiniz hedef {ambition}. seviyede; {laggard} tarafınız {weakest}. seviyede. Bu mesafe bir planlama sorunu değil: duyurulup sonra sessizce daraltılan dönüşümlerin en güvenilir habercisidir.',
      'Ya farkı finanse edin ya da dilinizi hedefinize indirin. İkisini birlikte taşımak, hem board nezdinde hem de değişmesini istediğiniz insanlar nezdinde itibarınızı aşındıran şeydir.',
    ],
    behindTitle: 'Hak ettiğinizden az iddia ediyorsunuz',
    behindBody: [
      'Pratikte, söylediğiniz hedefin ima ettiğinden daha ileridesiniz. Bu, tersinden iyi bir sorun; ama kurumun neye yeltendiğine bir tavan koyar, çünkü kimse dilin izin verdiğinden fazlasını teklif etmez.',
    ],
    laggards: ['karar ve sahiplik', 'icra', 'etkinin kanıtı'],
  },
}
